import { useCallback, useEffect, useRef } from "react";
import { useCreateLogMutation } from "src/api/services/logsApi";

type VersionPayload = { version: string; builtAt: string };

export function useAppUpdateRefresh(pollMs = 60_000, delayMs = 2_000) {
  const currentVersionRef = useRef<string | null>(null);
  const refreshScheduledRef = useRef<boolean>(false);
  const [createLog] = useCreateLogMutation();

  const fetchVersion = useCallback(async () => {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });

    if (!res.ok) return null;

    const data = (await res.json()) as VersionPayload;
    return data.version ?? null;
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshScheduledRef.current) return;
    refreshScheduledRef.current = true;

    setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.location.reload();
      } else {
        const onVis = () => {
          if (document.visibilityState === "visible") {
            window.location.reload();
            document.removeEventListener("visibilitychange", onVis);
          }
        };
        document.addEventListener("visibilitychange", onVis);
      }
    }, delayMs);
  }, [delayMs]);

  const check = useCallback(async () => {
    try {
      const v = await fetchVersion();
      if (!v) return;

      if (currentVersionRef.current == null) {
        currentVersionRef.current = v;
        return;
      }

      if (currentVersionRef.current !== v) {
        scheduleRefresh();
        createLog({
          level: "info",
          data: {
            message: `App version updated: ${v}`,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
          },
        });
      }
    } catch {
      return null;
    }
  }, [createLog, fetchVersion, scheduleRefresh]);

  useEffect(() => {
    check();

    const id = window.setInterval(check, pollMs);

    const onFocus = () => check();
    window.addEventListener("focus", onFocus);

    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [check, pollMs]);
}
